/**
 * Message Field Component for Medical On Demand
 *
 * Textarea input for entering the clinical question.
 */

import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type ProductTeamQuestionFieldProps = {
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
  placeholder?: string;
};

export function ProductTeamQuestionField({
  control,
  errors,
  isDisabled = false,
  placeholder = 'Ask your question, please be as specific as possible...',
}: ProductTeamQuestionFieldProps) {
  return (
    <Controller
      name="message"
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState, formState }) => {
        const showError =
          !!errors.message?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <FormControlLabel className="mb-2">
              <FormControlLabelText
                size="sm"
                className="font-semibold leading-normal text-typography-950">
                Message*
              </FormControlLabelText>
            </FormControlLabel>

            <Textarea className="min-h-56 self-stretch rounded-sm border border-outline-300 bg-background-0">
              <TextareaInput
                testID="mod-message-field"
                placeholder={placeholder}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                multiline
                textAlignVertical="top"
                className="p-3"
              />
            </Textarea>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.message?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
